import clsx from 'clsx'
import { useRef } from 'react'

import { ValidatedForm } from '@fv/client-components'
import { submitForm } from '@fv/client-core'
import type {
  LegacyReadOnlyDetail,
  ProductCatalogEntry,
  ProductMode,
} from '@fv/client-types'

import { FooterBar } from '@/components/FooterBar'
import { FormActions } from '@/components/forms/FormActions'
import InfoBox from '@/components/InfoBox'
import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { PageHeader } from '@/components/PageHeader'
import {
  useFreightClassOptions,
  useHazardClassOptions,
  usePackingGroupOptions,
  useSecondaryPackageOptions,
  yesNoOptions,
} from '@/hooks/settings/useEnumOptions'

import { buildProductCatalogEntry } from './commodityUtils'
import { FreightClassEstimate } from './FreightClassEstimate'
import { CfrHazmatTypeahead } from './load-items/CfrHazmatTypeahead'
import { mapCfrMaterialToProductFields } from './load-items/cfrHazmat'
import { PackagingSelector } from './PackagingSelector'
import { useCreateHandlingUnit } from './useCreateHandlingUnit'
import { useProductCatalog } from './useProductCatalog'
import { useProductCatalogForm } from './useProductCatalogForm'
import { useUpdateHandlingUnit } from './useUpdateHandlingUnit'

type Props = {
  initialValues?: ProductCatalogEntry
  mode: ProductMode
}

export const ProductCatalogForm = ({ initialValues, mode }: Props) => {
  const { handleChange, values, setValues } = useProductCatalogForm(
    mode,
    initialValues,
  )
  const { hazardClassOptions, hazardSubClassOptions } = useHazardClassOptions()
  const createProduct = useCreateHandlingUnit()
  const formRef = useRef<HTMLFormElement>(null)
  const freightClassOptions = useFreightClassOptions()
  const isEditing = !!initialValues
  const packingGroupOptions = usePackingGroupOptions()
  const productType = mode === 'ltl' ? 'LTL / Truckload' : 'parcel'
  const selectProduct = useProductCatalog(s => s.selectProduct)
  const showListView = useProductCatalog(s => s.showList)
  const unitOptions = useSecondaryPackageOptions(mode, values.package)
  const updateProduct = useUpdateHandlingUnit()
  const isLoading = createProduct.isPending || updateProduct.isPending

  function onValidSubmit() {
    if (isLoading) return

    const mutation = isEditing ? updateProduct : createProduct

    mutation.mutate(values, {
      onSuccess: handlingUnit => {
        const product = buildProductCatalogEntry(mode, handlingUnit)
        selectProduct(product)
      },
    })
  }

  const buildLockToggle = (name: keyof LegacyReadOnlyDetail) => (
    <InputAdornment className="bg-fv-blue-50">
      <input
        checked={values.readOnly[name]}
        disabled={!values[name]}
        name={`readOnly.${name}`}
        onChange={handleChange}
        type="checkbox"
      />
    </InputAdornment>
  )

  const getInputProps = (name: keyof LegacyReadOnlyDetail) => ({
    className: clsx({ 'bg-fv-beer-light': !!values[name] }),
    disabled: isLoading,
    name,
    onChange: handleChange,
    value: values[name],
  })

  const handleHazmatFieldChange = (
    name: keyof LegacyReadOnlyDetail,
    value: string,
  ) => {
    setValues({
      [name]: value,
      ...(value === ''
        ? { readOnly: { ...values.readOnly, [name]: false } }
        : {}),
    })
  }

  const packageProps = getInputProps('package')

  return (
    <ValidatedForm
      onValidSubmit={onValidSubmit}
      ref={formRef}
      className="flex flex-col flex-1 overflow-hidden"
    >
      <div className="p-6 overflow-auto">
        <PageHeader
          title={`${isEditing ? 'Edit' : 'Add'} ${productType} product`}
          noBorder
        />

        <InfoBox className="mb-6">
          Only enter information in the fields that you want to auto populate
          when selecting a product for quoting. Checking a field designates it
          as non-editable during the quoting process.
        </InfoBox>
        <div className="grid grid-cols-3 gap-x-2">
          <InputGroup
            className="col-span-full"
            endContent={buildLockToggle('description')}
            inputProps={getInputProps('description')}
            inputType="text"
            label="Description"
            required
          />

          <InputGroup
            endContent={buildLockToggle('package')}
            inputProps={packageProps}
            inputType="custom"
            label="Packaging"
          >
            <PackagingSelector
              allowCustom
              includeEmpty
              className={packageProps.className}
              length={Number(values.length)}
              workflow={mode}
              name={packageProps.name}
              value={values.package}
              width={Number(values.width)}
              onChange={val => {
                setValues({
                  package: val.type,
                  length: val.length,
                  width: val.width,
                })
              }}
            />
          </InputGroup>

          {mode === 'parcel' && (
            <InputGroup
              endContent={buildLockToggle('declaredValue')}
              inputProps={{
                ...getInputProps('declaredValue'),
                isPrice: true,
                min: 1,
              }}
              inputType="amount"
              label="Declared value"
              startContent={
                <InputAdornment icon="dollar-sign" position="start" />
              }
            />
          )}

          {mode === 'ltl' && (
            <InputGroup
              endContent={buildLockToggle('stackable')}
              inputProps={{
                ...getInputProps('stackable'),
                options: yesNoOptions,
              }}
              inputType="select"
              label="Stackable?"
            />
          )}

          {mode === 'ltl' && (
            <InputGroup
              endContent={buildLockToggle('pieces')}
              inputProps={{
                ...getInputProps('pieces'),
                min: 1,
              }}
              inputType="amount"
              label="Quantity"
            />
          )}

          <InputGroup
            endContent={
              <>
                <InputAdornment label="lbs." />
                {buildLockToggle('weight')}
              </>
            }
            inputProps={{
              ...getInputProps('weight'),
              min: 1,
            }}
            inputType="amount"
            label="Weight"
          />

          <div className="flex col-span-2 gap-x-1">
            <InputGroup
              endContent={buildLockToggle('length')}
              inputProps={{
                ...getInputProps('length'),
                min: 1,
              }}
              inputType="amount"
              label="L"
            />

            <InputGroup
              endContent={buildLockToggle('width')}
              inputProps={{
                ...getInputProps('width'),
                min: 1,
              }}
              inputType="amount"
              label="W"
            />

            <InputGroup
              endContent={
                <>
                  {buildLockToggle('height')}
                  <InputAdornment label="in." />
                </>
              }
              inputProps={{
                ...getInputProps('height'),
                min: 1,
              }}
              inputType="amount"
              label="H"
            />
          </div>

          {mode === 'ltl' && (
            <FreightClassEstimate
              weight={Number(values.weight)}
              height={Number(values.height)}
              width={Number(values.width)}
              length={Number(values.length)}
              quantity={Number(values.pieces)}
              containerClassName="form-group col-span-full"
            />
          )}

          {mode === 'parcel' && (
            <InputGroup
              endContent={buildLockToggle('nonStandard')}
              inputProps={{
                ...getInputProps('nonStandard'),
                options: yesNoOptions,
              }}
              inputType="select"
              label="Unboxed or crated?"
            />
          )}

          {mode === 'ltl' && (
            <>
              <InputGroup
                endContent={buildLockToggle('nmfc')}
                inputProps={getInputProps('nmfc')}
                inputType="text"
                label="NMFC item #"
              />

              <InputGroup
                endContent={buildLockToggle('freightClass')}
                inputProps={{
                  ...getInputProps('freightClass'),
                  options: freightClassOptions,
                }}
                inputType="select"
                label="Freight class"
              />

              <InputGroup
                endContent={buildLockToggle('saidToContain')}
                inputProps={{
                  ...getInputProps('saidToContain'),
                  min: 1,
                }}
                inputType="amount"
                label="Contains"
              />

              <InputGroup
                endContent={buildLockToggle('saidToContainPackagingType')}
                inputProps={{
                  ...getInputProps('saidToContainPackagingType'),
                  options: unitOptions,
                }}
                inputType="select"
                label="Units"
              />

              <InputGroup
                endContent={buildLockToggle('hazardous')}
                inputProps={{
                  ...getInputProps('hazardous'),
                  options: yesNoOptions,
                }}
                inputType="select"
                label="Hazmat?"
              />
            </>
          )}

          {mode === 'ltl' && values.hazardous === 'Yes' && (
            <>
              <InputGroup
                className="col-span-3"
                endContent={buildLockToggle('properShippingName')}
                inputProps={getInputProps('properShippingName')}
                inputType="custom"
                label="Proper shipping name"
              >
                <CfrHazmatTypeahead
                  field="properShippingName"
                  name="properShippingName"
                  value={values.properShippingName}
                  disabled={isLoading}
                  onChange={val =>
                    handleHazmatFieldChange('properShippingName', val)
                  }
                  onSelectMaterial={material =>
                    setValues(mapCfrMaterialToProductFields(material))
                  }
                />
              </InputGroup>

              <InputGroup
                endContent={buildLockToggle('hazmatId')}
                inputProps={getInputProps('hazmatId')}
                inputType="custom"
                label="Hazmat ID"
              >
                <CfrHazmatTypeahead
                  field="identificationNumber"
                  name="hazmatId"
                  value={values.hazmatId}
                  disabled={isLoading}
                  onChange={val => handleHazmatFieldChange('hazmatId', val)}
                  onSelectMaterial={material =>
                    setValues(mapCfrMaterialToProductFields(material))
                  }
                />
              </InputGroup>

              <InputGroup
                endContent={buildLockToggle('hazardClass')}
                inputProps={{
                  ...getInputProps('hazardClass'),
                  options: hazardClassOptions,
                }}
                inputType="select"
                label="Hazard class"
              />

              <InputGroup
                endContent={buildLockToggle('packingGroup')}
                inputProps={{
                  ...getInputProps('packingGroup'),
                  options: packingGroupOptions,
                }}
                inputType="select"
                label="Packing group"
              />

              <InputGroup
                endContent={buildLockToggle('secondaryHazardClass')}
                inputProps={{
                  ...getInputProps('secondaryHazardClass'),
                  options: hazardSubClassOptions,
                }}
                inputType="select"
                label="Sub hazard class 1"
              />

              <InputGroup
                endContent={buildLockToggle('tertiaryHazardClass')}
                inputProps={{
                  ...getInputProps('tertiaryHazardClass'),
                  options: hazardSubClassOptions,
                }}
                inputType="select"
                label="Sub hazard class 2"
              />

              <InputGroup
                endContent={buildLockToggle('specialPermit')}
                inputProps={getInputProps('specialPermit')}
                inputType="text"
                label="Special permit #"
              />

              <InputGroup
                className="col-span-3"
                endContent={buildLockToggle('technicalName')}
                inputProps={getInputProps('technicalName')}
                inputType="text"
                label="Technical name"
              />

              <InputGroup
                endContent={buildLockToggle('reportableQuantity')}
                inputProps={{
                  ...getInputProps('reportableQuantity'),
                  options: yesNoOptions,
                }}
                inputType="select"
                label="Reportable quantity?"
              />

              <InputGroup
                endContent={buildLockToggle('residueLastContained')}
                inputProps={{
                  ...getInputProps('residueLastContained'),
                  options: yesNoOptions,
                }}
                inputType="select"
                label="Residue last contained?"
              />

              <InputGroup
                endContent={buildLockToggle('limitedQuantity')}
                inputProps={{
                  ...getInputProps('limitedQuantity'),
                  options: yesNoOptions,
                }}
                inputType="select"
                label="Limited quantity?"
              />

              <InputGroup
                endContent={buildLockToggle('marinePollutant')}
                inputProps={{
                  ...getInputProps('marinePollutant'),
                  options: yesNoOptions,
                }}
                inputType="select"
                label="Marine pollutant?"
              />
            </>
          )}
        </div>
      </div>
      <FooterBar>
        <FormActions
          onCancel={() => showListView()}
          onSubmit={() => submitForm(formRef.current)}
          submitText="Save and apply"
        />
      </FooterBar>
    </ValidatedForm>
  )
}
